/** @type {string} */
export const LIVESEARCH_FILL_MOCK_CLASS_ID = 'demo-class';

/** @type {string} */
export const LIVESEARCH_FILL_MOCK_CONTEXTE = 'demo';

/**
 * Build a mock livesearch-fill URL for catalog results.
 * @param {string} resultId
 * @param {'edit' | 'structure' | 'contact'} action
 * @returns {string}
 */
export function buildMockLiveSearchFillUrl(resultId, action) {
  const params = new URLSearchParams({
    id: resultId,
    contexte: LIVESEARCH_FILL_MOCK_CONTEXTE,
    action,
  });
  return `/livesearch-fill/${LIVESEARCH_FILL_MOCK_CLASS_ID}?${params.toString()}`;
}

/**
 * @param {string} contactId
 * @param {string} fullName
 * @param {string} organisation
 * @returns {ApiLiveSearchActionField[]}
 */
function buildDefaultFillFields(contactId, fullName, organisation) {
  return [
    { id: 'ls_contact', values: [contactId], singleValue: true },
    { id: 'ls_name', values: [fullName], singleValue: true },
    { id: 'ls_org', values: [organisation], singleValue: true },
  ];
}

/** Params returned by edit livesearch-fill actions (override form submission params). */
export const MOCK_LIVESEARCH_FILL_EDIT_PARAMS = [
  { id: 'edit_mode', values: ['1'], singleValue: true },
];

/** Static mock responses for mock-live-search.js fixtures */
export const mockLiveSearchFillResponsesByUrl = {
  '/livesearch-fill/demo-class?id=result-1&contexte=demo&action=contact': {
    fields: [
      { id: 'ls_contact', values: ['42'], singleValue: true },
      { id: 'ls_name', values: ['Jean Dupont'], singleValue: true },
    ],
    params: null,
    editSendFormUrl: null,
  },
  '/livesearch-fill/demo-class?id=result-3&contexte=demo&action=edit': {
    fields: [
      { id: 'ls_contact', values: ['77'], singleValue: true },
      { id: 'ls_name', values: ['Pierre Bernard'], singleValue: true },
      { id: 'ls_org', values: ['Zenith Productions'], singleValue: true },
    ],
    params: MOCK_LIVESEARCH_FILL_EDIT_PARAMS,
    editSendFormUrl: '/form/submit/live-search-edit',
  },
  '/livesearch-fill/demo-class?id=result-a&contexte=demo&action=contact': {
    fields: [{ id: 'ls_contact', values: ['10'], singleValue: true }],
    params: null,
    editSendFormUrl: null,
  },
  '/livesearch-fill/demo-class?id=result-b&contexte=demo&action=contact': {
    fields: [{ id: 'ls_name', values: ['Sophie Leroy'], singleValue: true }],
    params: null,
    editSendFormUrl: null,
  },
  '/livesearch-fill/demo-class?id=result-c&contexte=demo&action=contact': {
    fields: [
      { id: 'ls_contact', values: ['55'], singleValue: true },
      { id: 'ls_org', values: ['Agence XYZ'], singleValue: true },
    ],
    params: null,
    editSendFormUrl: null,
  },
};

/**
 * @param {string} fieldsUrl
 * @returns {ApiLiveSearchFillResponse | null}
 */
export function getMockLiveSearchFillResponse(fieldsUrl) {
  if (!fieldsUrl) return null;

  const url = new URL(fieldsUrl, 'http://mock.local');
  const resultId = url.searchParams.get('id');
  const action = url.searchParams.get('action');

  if (!resultId || !action) return null;

  const match = resultId.match(/^contact-ls-(\d+)$/);
  if (match) {
    const index = Number(match[1]) - 1;
    const firstNames = ['Jean', 'Marie', 'Paul', 'Sophie', 'Lucas'];
    const lastNames = ['Dupont', 'Martin', 'Bernard', 'Leroy', 'Moreau'];
    const organisations = ['Heeds', 'Zenith Productions', 'Olympia Events', 'Bataclan Agency', 'Trabendo Live'];

    const firstName = firstNames[index % firstNames.length];
    const lastName = lastNames[Math.floor(index / firstNames.length) % lastNames.length];
    const organisation = organisations[index % organisations.length];
    const fullName = `${firstName} ${lastName}`;
    const contactId = String(index + 1);

    if (action === 'edit') {
      return {
        fields: buildDefaultFillFields(contactId, fullName, organisation),
        params: MOCK_LIVESEARCH_FILL_EDIT_PARAMS,
        editSendFormUrl: '/form/submit/live-search-edit',
      };
    }

    if (action === 'contact') {
      return {
        fields: [{ id: 'ls_contact', values: [contactId], singleValue: true }],
        params: null,
        editSendFormUrl: null,
      };
    }

    if (action === 'structure') {
      return {
        fields: [{ id: 'ls_org', values: [organisation], singleValue: true }],
        params: null,
        editSendFormUrl: null,
      };
    }
  }

  return mockLiveSearchFillResponsesByUrl[fieldsUrl] ?? null;
}

/**
 * @param {string} fieldsUrl
 * @returns {Promise<ApiLiveSearchFillResponse | null>}
 */
export async function getLiveSearchFillMockResponse(fieldsUrl) {
  const response = getMockLiveSearchFillResponse(fieldsUrl);
  if (!response) return null;

  await new Promise((resolve) => { setTimeout(resolve, 200); });
  return response;
}
